'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useMarketData } from '@/contexts/MarketDataContext';
import { formatINR, formatINRCompact, calculateCharges } from '@/lib/india-market';
import { RefreshCw, Save, Calendar, BarChart2, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { markRenderStart, markRenderEnd } from '@/lib/tick-latency';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SettlementRow {
  id?: string;
  symbol: string;
  companyName: string;
  isin: string;
  segment: string;
  qty: number;
  avgPrice: number;
  closePrice: number;
  prevClose: number;
  investedValue: number;
  marketValue: number;
  mtmPnl: number;
  unrealizedPnl: number;
  unrealizedPnlPct: number;
  realizedPnl: number;
  brokerage: number;
  stt: number;
  exchangeCharges: number;
  sebiFee: number;
  stampDuty: number;
  gst: number;
  totalCharges: number;
  netPnl: number;
  holdingDays: number;
  taxType: string;
}

// ─── Holdings base data (mirrors HoldingsTable) ───────────────────────────────

const HOLDINGS_BASE = [
  { symbol: 'TCS',        companyName: 'Tata Consultancy Services', qty: 15,  avgPrice: 3876.50, holdingDays: 420, isin: 'INE467B01029' },
  { symbol: 'INFY',       companyName: 'Infosys Ltd',               qty: 30,  avgPrice: 1487.20, holdingDays: 380, isin: 'INE009A01021' },
  { symbol: 'HDFCBANK',   companyName: 'HDFC Bank Ltd',             qty: 20,  avgPrice: 1923.40, holdingDays: 290, isin: 'INE040A01034' },
  { symbol: 'RELIANCE',   companyName: 'Reliance Industries Ltd',   qty: 8,   avgPrice: 2645.00, holdingDays: 510, isin: 'INE002A01018' },
  { symbol: 'AXISBANK',   companyName: 'Axis Bank Ltd',             qty: 25,  avgPrice: 1089.60, holdingDays: 310, isin: 'INE238A01034' },
  { symbol: 'BAJFINANCE', companyName: 'Bajaj Finance Ltd',         qty: 3,   avgPrice: 6789.00, holdingDays: 180, isin: 'INE296A01024' },
  { symbol: 'SUNPHARMA',  companyName: 'Sun Pharmaceutical',        qty: 18,  avgPrice: 1934.70, holdingDays: 240, isin: 'INE044A01036' },
  { symbol: 'MARUTI',     companyName: 'Maruti Suzuki India',       qty: 2,   avgPrice: 11240.00,holdingDays: 400, isin: 'INE585B01010' },
  { symbol: 'WIPRO',      companyName: 'Wipro Ltd',                 qty: 60,  avgPrice: 534.20,  holdingDays: 350, isin: 'INE075A01022' },
  { symbol: 'ONGC',       companyName: 'Oil & Natural Gas Corp',    qty: 80,  avgPrice: 271.40,  holdingDays: 460, isin: 'INE213A01029' },
  { symbol: 'TATASTEEL',  companyName: 'Tata Steel Ltd',            qty: 150, avgPrice: 182.30,  holdingDays: 200, isin: 'INE081A01012' },
  { symbol: 'ICICIBANK',  companyName: 'ICICI Bank Ltd',            qty: 22,  avgPrice: 1187.80, holdingDays: 330, isin: 'INE090A01021' },
];

const FALLBACK_LTP: Record<string, number> = {
  TCS: 4123.80, INFY: 1654.90, HDFCBANK: 1876.25, RELIANCE: 2987.45,
  AXISBANK: 1187.40, BAJFINANCE: 7234.80, SUNPHARMA: 1876.60, MARUTI: 12456.00,
  WIPRO: 567.35, ONGC: 287.60, TATASTEEL: 167.85, ICICIBANK: 1243.60,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildSettlementRow(
  base: typeof HOLDINGS_BASE[0],
  ltp: number,
  prevClose: number
): SettlementRow {
  const { symbol, companyName, qty, avgPrice, holdingDays, isin } = base;
  const closePrice = ltp;
  const investedValue = parseFloat((avgPrice * qty).toFixed(2));
  const marketValue  = parseFloat((closePrice * qty).toFixed(2));
  const unrealizedPnl    = parseFloat((marketValue - investedValue).toFixed(2));
  const unrealizedPnlPct = investedValue > 0 ? parseFloat(((unrealizedPnl / investedValue) * 100).toFixed(2)) : 0;
  const mtmPnl = prevClose > 0 ? parseFloat(((closePrice - prevClose) * qty).toFixed(2)) : 0;

  // Charges: delivery buy side (CNC)
  const buyCharges = calculateCharges({ side: 'BUY', segment: 'EQ', qty, price: avgPrice });
  const sellCharges = calculateCharges({ side: 'SELL', segment: 'EQ', qty, price: closePrice });
  const brokerage = parseFloat((buyCharges.brokerage + sellCharges.brokerage).toFixed(2));
  const stt = parseFloat((buyCharges.stt + sellCharges.stt).toFixed(2));
  const exchangeCharges = parseFloat((buyCharges.exchangeTxnCharge + sellCharges.exchangeTxnCharge).toFixed(2));
  const sebiFee = parseFloat((buyCharges.sebiTurnoverFee + sellCharges.sebiTurnoverFee).toFixed(4));
  const stampDuty = parseFloat((buyCharges.stampDuty + sellCharges.stampDuty).toFixed(2));
  const gst = parseFloat((buyCharges.gst + sellCharges.gst).toFixed(2));
  const totalCharges = parseFloat((brokerage + stt + exchangeCharges + sebiFee + stampDuty + gst).toFixed(2));
  const netPnl = parseFloat((unrealizedPnl - totalCharges).toFixed(2));
  const taxType = holdingDays > 365 ? 'LTCG' : 'STCG';

  return {
    symbol, companyName, isin, segment: 'EQ', qty, avgPrice,
    closePrice, prevClose, investedValue, marketValue, mtmPnl,
    unrealizedPnl, unrealizedPnlPct, realizedPnl: 0,
    brokerage, stt, exchangeCharges, sebiFee, stampDuty, gst,
    totalCharges, netPnl, holdingDays, taxType,
  };
}

function pnlClass(val: number) {
  return val >= 0 ? 'text-positive' : 'text-negative';
}

function pnlSign(val: number) {
  return val >= 0 ? '+' : '';
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function EODSettlementContent() {
  const { user } = useAuth();
  const supabase = createClient();
  const { ticks, subscribe, unsubscribe, tickReceivedAt } = useMarketData();

  const [rows, setRows] = useState<SettlementRow[]>([]);
  const [savedIds, setSavedIds] = useState<Record<string, string>>({});
  const [settlementDate, setSettlementDate] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // ── Latency: track render start per symbol ────────────────────────────────
  const renderStartRef = useRef<Record<string, number>>({});
  const symbols = HOLDINGS_BASE.map((h) => h.symbol);
  symbols.forEach((sym) => {
    if (tickReceivedAt[sym]) {
      renderStartRef.current[sym] = markRenderStart(sym, 'eod-settlement');
    }
  });

  // Set date on client only to avoid hydration mismatch
  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    setSettlementDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  // Subscribe to live ticks
  useEffect(() => {
    subscribe(symbols);
    return () => unsubscribe(symbols);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute rows from live ticks
  useEffect(() => {
    const computed = HOLDINGS_BASE.map(base => {
      const tick = ticks[base.symbol];
      const ltp = tick?.ltp ?? FALLBACK_LTP[base.symbol] ?? base.avgPrice;
      const prevClose = tick?.prevClose ?? ltp;
      return buildSettlementRow(base, ltp, prevClose);
    });
    setRows(computed);
  }, [ticks]);

  // ── Latency: close measurement after paint ────────────────────────────────
  useEffect(() => {
    symbols.forEach((sym) => {
      const receivedAt = tickReceivedAt[sym];
      const renderStart = renderStartRef.current[sym];
      if (receivedAt && renderStart) {
        markRenderEnd(sym, 'eod-settlement', receivedAt, renderStart);
      }
    });
  });

  // Load existing saved settlement for today
  const loadSavedSettlement = useCallback(async (date: string) => {
    if (!user || !date) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from('eod_settlement')
        .select('*')
        .eq('user_id', user.id)
        .eq('settlement_date', date);

      if (fetchErr) throw new Error(fetchErr.message);

      if (data && data.length > 0) {
        const idMap: Record<string, string> = {};
        data.forEach((row: Record<string, unknown>) => {
          idMap[row.symbol as string] = row.id as string;
        });
        setSavedIds(idMap);
      } else {
        setSavedIds({});
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load settlement data');
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    if (settlementDate) {
      loadSavedSettlement(settlementDate);
    }
  }, [settlementDate, loadSavedSettlement]);

  const handleSave = async () => {
    if (!user || !settlementDate) return;
    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      const upsertRows = rows.map(r => ({
        user_id: user.id,
        settlement_date: settlementDate,
        symbol: r.symbol,
        company_name: r.companyName,
        isin: r.isin,
        segment: r.segment,
        qty: r.qty,
        avg_price: r.avgPrice,
        close_price: r.closePrice,
        prev_close: r.prevClose,
        invested_value: r.investedValue,
        market_value: r.marketValue,
        mtm_pnl: r.mtmPnl,
        unrealized_pnl: r.unrealizedPnl,
        unrealized_pnl_pct: r.unrealizedPnlPct,
        realized_pnl: r.realizedPnl,
        brokerage: r.brokerage,
        stt: r.stt,
        exchange_charges: r.exchangeCharges,
        sebi_fee: r.sebiFee,
        stamp_duty: r.stampDuty,
        gst: r.gst,
        total_charges: r.totalCharges,
        net_pnl: r.netPnl,
        holding_days: r.holdingDays,
        tax_type: r.taxType,
      }));

      const { error: upsertErr } = await supabase
        .from('eod_settlement')
        .upsert(upsertRows, { onConflict: 'user_id,symbol,settlement_date' });

      if (upsertErr) throw new Error(upsertErr.message);

      setSaveSuccess(true);
      await loadSavedSettlement(settlementDate);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save settlement');
    } finally {
      setSaving(false);
    }
  };

  // ─── Aggregates ─────────────────────────────────────────────────────────────

  const totalInvested    = rows.reduce((s, r) => s + r.investedValue, 0);
  const totalMarket      = rows.reduce((s, r) => s + r.marketValue, 0);
  const totalUnrealized  = rows.reduce((s, r) => s + r.unrealizedPnl, 0);
  const totalRealized    = rows.reduce((s, r) => s + r.realizedPnl, 0);
  const totalMtm         = rows.reduce((s, r) => s + r.mtmPnl, 0);
  const totalChargesSum  = rows.reduce((s, r) => s + r.totalCharges, 0);
  const totalNetPnl      = rows.reduce((s, r) => s + r.netPnl, 0);
  const totalBrokerage   = rows.reduce((s, r) => s + r.brokerage, 0);
  const totalSTT         = rows.reduce((s, r) => s + r.stt, 0);
  const totalExch        = rows.reduce((s, r) => s + r.exchangeCharges, 0);
  const totalGST         = rows.reduce((s, r) => s + r.gst, 0);
  const savedCount       = Object.keys(savedIds).length;

  return (
    <div className="flex flex-col h-full bg-background text-foreground">
      {/* ── Header ── */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BarChart2 size={18} className="text-primary" />
          <h1 className="text-sm font-bold text-foreground">End-of-Day Settlement</h1>
          {savedCount > 0 && (
            <span className="text-2xs bg-positive/10 text-positive px-2 py-0.5 rounded-full font-semibold">
              {savedCount} saved
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-muted/40 border border-border rounded-md px-2 py-1">
            <Calendar size={13} className="text-muted-foreground" />
            <input
              type="date"
              value={settlementDate}
              onChange={e => setSettlementDate(e.target.value)}
              className="bg-transparent text-xs text-foreground outline-none w-28"
            />
          </div>
          <button
            onClick={() => settlementDate && loadSavedSettlement(settlementDate)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-muted/40 border border-border text-xs text-muted-foreground hover:text-foreground transition-colors"
            title="Refresh"
          >
            <RefreshCw size={13} />
          </button>
          <button
            onClick={handleSave}
            disabled={saving || rows.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <Save size={13} />
            {saving ? 'Saving…' : 'Save Settlement'}
          </button>
        </div>
      </div>

      {/* ── Status messages ── */}
      {saveSuccess && (
        <div className="mx-4 mt-3 flex items-center gap-2 bg-positive/10 border border-positive/20 rounded-md px-3 py-2 text-xs text-positive">
          <CheckCircle2 size={13} />
          Settlement saved successfully for {settlementDate}
        </div>
      )}
      {error && (
        <div className="mx-4 mt-3 flex items-center gap-2 bg-negative/10 border border-negative/20 rounded-md px-3 py-2 text-xs text-negative">
          <AlertCircle size={13} />
          {error}
        </div>
      )}

      {/* ── Summary Cards ── */}
      <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {[
          { label: 'Invested', value: totalInvested, compact: true, neutral: true },
          { label: 'Market Value', value: totalMarket, compact: true, neutral: true },
          { label: 'Unrealized P&L', value: totalUnrealized, compact: false, neutral: false },
          { label: 'Realized P&L', value: totalRealized, compact: false, neutral: false },
          { label: "Day's MTM", value: totalMtm, compact: false, neutral: false },
          { label: 'Total Charges', value: -totalChargesSum, compact: false, neutral: false },
          { label: 'Net P&L', value: totalNetPnl, compact: false, neutral: false },
        ].map(card => (
          <div key={card.label} className="bg-card border border-border rounded-lg px-3 py-2.5">
            <p className="text-2xs text-muted-foreground mb-1">{card.label}</p>
            <p className={`text-sm font-bold font-tabular ${card.neutral ? 'text-foreground' : pnlClass(card.value)}`}>
              {card.neutral
                ? formatINRCompact(card.value)
                : `${pnlSign(card.value)}${formatINRCompact(Math.abs(card.value))}`}
            </p>
          </div>
        ))}
      </div>

      {/* ── Charges Summary ── */}
      <div className="px-4 pb-2">
        <div className="bg-muted/20 border border-border rounded-lg px-3 py-2 flex flex-wrap gap-x-4 gap-y-1 text-2xs text-muted-foreground">
          <span className="font-semibold text-foreground text-xs">Charges Breakdown:</span>
          <span>Brokerage: <span className="text-foreground font-tabular">{formatINR(totalBrokerage)}</span></span>
          <span>STT: <span className="text-foreground font-tabular">{formatINR(totalSTT)}</span></span>
          <span>Exch. Charges: <span className="text-foreground font-tabular">{formatINR(totalExch)}</span></span>
          <span>GST: <span className="text-foreground font-tabular">{formatINR(totalGST)}</span></span>
          <span className="font-semibold text-negative">Total: <span className="font-tabular">{formatINR(totalChargesSum)}</span></span>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="flex-1 overflow-auto px-4 pb-4">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
            Loading settlement data…
          </div>
        ) : (
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  {[
                    'Symbol', 'Qty', 'Avg Price', 'Close', 'Prev Close',
                    'Invested', 'Mkt Value', 'MTM P&L',
                    'Unrealized P&L', 'Realized P&L',
                    'Charges', 'Net P&L', 'Tax', 'Status'
                  ].map(h => (
                    <th key={h} className="text-left px-2.5 py-2 text-2xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const isSaved = !!savedIds[row.symbol];
                  const isExpanded = expandedRow === row.symbol;
                  return (
                    <React.Fragment key={row.symbol}>
                      <tr
                        className="border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer"
                        onClick={() => setExpandedRow(isExpanded ? null : row.symbol)}
                      >
                        <td className="px-2.5 py-2">
                          <p className="font-semibold text-foreground">{row.symbol}</p>
                          <p className="text-2xs text-muted-foreground max-w-[100px] truncate">{row.companyName}</p>
                        </td>
                        <td className="px-2.5 py-2 font-tabular text-foreground">{row.qty}</td>
                        <td className="px-2.5 py-2 font-tabular text-foreground">{formatINR(row.avgPrice)}</td>
                        <td className="px-2.5 py-2 font-tabular font-semibold text-foreground">{formatINR(row.closePrice)}</td>
                        <td className="px-2.5 py-2 font-tabular text-muted-foreground">{formatINR(row.prevClose)}</td>
                        <td className="px-2.5 py-2 font-tabular text-muted-foreground">{formatINR(row.investedValue)}</td>
                        <td className="px-2.5 py-2 font-tabular text-foreground">{formatINR(row.marketValue)}</td>
                        <td className={`px-2.5 py-2 font-tabular font-semibold ${pnlClass(row.mtmPnl)}`}>
                          {pnlSign(row.mtmPnl)}{formatINR(Math.abs(row.mtmPnl))}
                        </td>
                        <td className={`px-2.5 py-2 font-tabular font-semibold ${pnlClass(row.unrealizedPnl)}`}>
                          <div>{pnlSign(row.unrealizedPnl)}{formatINR(Math.abs(row.unrealizedPnl))}</div>
                          <div className="text-2xs opacity-70">{pnlSign(row.unrealizedPnlPct)}{row.unrealizedPnlPct.toFixed(2)}%</div>
                        </td>
                        <td className={`px-2.5 py-2 font-tabular font-semibold ${pnlClass(row.realizedPnl)}`}>
                          {pnlSign(row.realizedPnl)}{formatINR(Math.abs(row.realizedPnl))}
                        </td>
                        <td className="px-2.5 py-2 font-tabular text-negative">
                          -{formatINR(row.totalCharges)}
                        </td>
                        <td className={`px-2.5 py-2 font-tabular font-bold ${pnlClass(row.netPnl)}`}>
                          {pnlSign(row.netPnl)}{formatINR(Math.abs(row.netPnl))}
                        </td>
                        <td className="px-2.5 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-2xs font-semibold ${
                            row.taxType === 'LTCG' ? 'bg-positive/10 text-positive' : 'bg-warning/10 text-warning'
                          }`}>
                            {row.taxType}
                          </span>
                          <p className="text-2xs text-muted-foreground mt-0.5">{row.holdingDays}d</p>
                        </td>
                        <td className="px-2.5 py-2">
                          {isSaved ? (
                            <span className="flex items-center gap-1 text-positive text-2xs font-semibold">
                              <CheckCircle2 size={11} /> Saved
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-2xs">Unsaved</span>
                          )}
                        </td>
                      </tr>
                      {/* Expanded charges detail */}
                      {isExpanded && (
                        <tr className="bg-muted/10 border-b border-border/30">
                          <td colSpan={14} className="px-4 py-3">
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-2xs">
                              <span className="font-semibold text-foreground text-xs mb-1 w-full flex items-center gap-1">
                                <Info size={12} className="text-primary" />
                                Charges Detail — {row.symbol}
                              </span>
                              <span className="text-muted-foreground">Brokerage: <span className="text-foreground font-tabular">{formatINR(row.brokerage)}</span></span>
                              <span className="text-muted-foreground">STT: <span className="text-foreground font-tabular">{formatINR(row.stt)}</span></span>
                              <span className="text-muted-foreground">Exch. Charges: <span className="text-foreground font-tabular">{formatINR(row.exchangeCharges)}</span></span>
                              <span className="text-muted-foreground">SEBI Fee: <span className="text-foreground font-tabular">₹{row.sebiFee.toFixed(4)}</span></span>
                              <span className="text-muted-foreground">Stamp Duty: <span className="text-foreground font-tabular">{formatINR(row.stampDuty)}</span></span>
                              <span className="text-muted-foreground">GST (18%): <span className="text-foreground font-tabular">{formatINR(row.gst)}</span></span>
                              <span className="font-semibold text-negative">Total Charges: <span className="font-tabular">{formatINR(row.totalCharges)}</span></span>
                              <span className="font-semibold">Net P&L: <span className={`font-tabular ${pnlClass(row.netPnl)}`}>{pnlSign(row.netPnl)}{formatINR(Math.abs(row.netPnl))}</span></span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-muted/30">
                  <td className="px-2.5 py-2 font-bold text-foreground text-xs" colSpan={5}>Total</td>
                  <td className="px-2.5 py-2 font-tabular font-bold text-foreground">{formatINR(totalInvested)}</td>
                  <td className="px-2.5 py-2 font-tabular font-bold text-foreground">{formatINR(totalMarket)}</td>
                  <td className={`px-2.5 py-2 font-tabular font-bold ${pnlClass(totalMtm)}`}>
                    {pnlSign(totalMtm)}{formatINR(Math.abs(totalMtm))}
                  </td>
                  <td className={`px-2.5 py-2 font-tabular font-bold ${pnlClass(totalUnrealized)}`}>
                    {pnlSign(totalUnrealized)}{formatINR(Math.abs(totalUnrealized))}
                  </td>
                  <td className={`px-2.5 py-2 font-tabular font-bold ${pnlClass(totalRealized)}`}>
                    {pnlSign(totalRealized)}{formatINR(Math.abs(totalRealized))}
                  </td>
                  <td className="px-2.5 py-2 font-tabular font-bold text-negative">-{formatINR(totalChargesSum)}</td>
                  <td className={`px-2.5 py-2 font-tabular font-bold ${pnlClass(totalNetPnl)}`}>
                    {pnlSign(totalNetPnl)}{formatINR(Math.abs(totalNetPnl))}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── Footer note ── */}
      <div className="px-4 py-2 border-t border-border bg-muted/10 flex items-start gap-2">
        <Info size={12} className="text-muted-foreground mt-0.5 flex-shrink-0" />
        <p className="text-2xs text-muted-foreground leading-relaxed">
          <span className="font-semibold text-foreground">Settlement Note:</span> Charges calculated per SEBI/NSE rules (FY 2025-26). 
          LTCG (&gt;12 months) @ 12.5% with ₹1.25L exemption. STCG (≤12 months) @ 20%. 
          Click any row to expand charges detail. Save to persist settlement snapshot to Supabase.
        </p>
      </div>
    </div>
  );
}
